import { useState } from 'react';

import { AlertCircle, CalendarX2, Plus, Tag } from 'lucide-react';
import { toast } from 'sonner';

import { formatarDinheiro, paraCentavos } from '@/nucleo/dinheiro';
import { formatarData } from '@/nucleo/formato/datas';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { MoneyInput } from '@/ui/MoneyInput';
import { PageHeader } from '@/ui/PageHeader';
import { PaginacaoCompacta } from '@/ui/PaginacaoCompacta';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Spinner } from '@/ui/Spinner';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui/base/table';

import {
  ROTULO_DA_SITUACAO,
  ROTULO_DO_SERVICO,
  TIPOS_DE_SERVICO_ADMINISTRATIVO,
  type PrecoPublico,
  type SituacaoDoPreco,
  type TipoDeServicoAdministrativo,
} from './contrato';
import {
  TAMANHO_DA_PAGINA,
  mensagemDoErro,
  useCadastrarPreco,
  useEncerrarVigencia,
  usePrecosPublicos,
} from './dados';

const TODOS = 'todos';

const VARIANTE_DA_SITUACAO: Record<SituacaoDoPreco, 'default' | 'secondary' | 'outline'> = {
  VIGENTE: 'default',
  AGENDADO: 'outline',
  ENCERRADO: 'secondary',
  INATIVO: 'outline',
};

/**
 * `/admin/parametros/precos-publicos` — a tabela de preços públicos dos serviços
 * administrativos, base do aviso de "preço público vigente" na tela de Cobrança.
 *
 * DADO REAL, DA API (`bff/v1/admin/precos-publicos`, migração 0320). Duas ações
 * gravam de verdade: cadastrar um preço e encerrar a vigência de um preço. A
 * regra mora no banco — entre os preços de um mesmo serviço as vigências não se
 * sobrepõem — e a tela mostra a recusa na frase que a API devolve.
 *
 * NÃO HÁ "EDITAR" NEM "EXCLUIR". Valor, serviço e início de um preço são o que as
 * cobranças já emitidas citaram; mudar um preço é cadastrar outro, e o anterior
 * termina na véspera. O que o usuário não pode fazer não é renderizado (regra 7
 * das regras de interface): "Encerrar vigência" só aparece em preço vigente ou
 * agendado.
 */
export function PrecosPublicosPage() {
  const [pagina, setPagina] = useState(1);
  const [servico, setServico] = useState<TipoDeServicoAdministrativo | null>(null);
  const { data, isLoading, isError, error, refetch } = usePrecosPublicos(pagina, servico);
  const [novoAberto, setNovoAberto] = useState(false);
  const [emEncerramento, setEmEncerramento] = useState<PrecoPublico | null>(null);

  const precos = data?.itens ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Preços públicos"
        description="Valores cobrados pelos serviços administrativos prestados ao credenciado, com vigência e decreto de referência."
      >
        <Button onClick={() => setNovoAberto(true)}>
          <Plus className="h-4 w-4 sm:mr-2" aria-hidden="true" />
          <span className="hidden sm:inline">Novo preço</span>
          <span className="sr-only sm:hidden">Novo preço</span>
        </Button>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Preços cadastrados</CardTitle>
          <CardDescription>
            Um serviço pode ter mais de um preço ao longo do tempo; vale o que estiver vigente na
            data do fato gerador da cobrança.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid max-w-sm gap-2">
            <Label htmlFor="preco-filtro-servico">Filtrar por serviço</Label>
            <Select
              value={servico ?? TODOS}
              onValueChange={(escolha) => {
                setServico(escolha === TODOS ? null : (escolha as TipoDeServicoAdministrativo));
                setPagina(1);
              }}
            >
              <SelectTrigger id="preco-filtro-servico">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS}>Todos os serviços</SelectItem>
                {TIPOS_DE_SERVICO_ADMINISTRATIVO.map((tipo) => (
                  <SelectItem key={tipo} value={tipo}>
                    {ROTULO_DO_SERVICO[tipo]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isError ? (
            <ErrorState
              variant="load-failed"
              description={mensagemDoErro(
                error,
                'Não foi possível buscar os preços públicos agora. Verifique sua conexão e tente novamente.',
              )}
              primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
            />
          ) : isLoading ? (
            <Spinner />
          ) : precos.length === 0 ? (
            <EmptyState
              icon={Tag}
              title="Nenhum preço público cadastrado"
              description={
                servico
                  ? 'Este serviço ainda não tem preço. Use "Novo preço" para cadastrar.'
                  : 'Cadastre o valor de cada serviço administrativo para que as cobranças possam ser calculadas.'
              }
            />
          ) : (
            <>
              <Table aria-label="Preços públicos">
                <TableHeader>
                  <TableRow>
                    <TableHead>Serviço</TableHead>
                    <TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Vigência</TableHead>
                    <TableHead>Decreto</TableHead>
                    <TableHead>Situação</TableHead>
                    <TableHead>
                      <span className="sr-only">Ações</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {precos.map((preco) => (
                    <TableRow key={preco.id}>
                      <TableCell className="font-medium">
                        {ROTULO_DO_SERVICO[preco.tipoServico]}
                      </TableCell>
                      <TableCell className="text-sm">{preco.descricao}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatarDinheiro(preco.valor)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatarData(preco.vigenciaInicio)}
                        {preco.vigenciaFim
                          ? ` até ${formatarData(preco.vigenciaFim)}`
                          : ' — sem fim'}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {preco.decretoReferencia ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={VARIANTE_DA_SITUACAO[preco.situacao]}>
                          {ROTULO_DA_SITUACAO[preco.situacao]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {podeEncerrar(preco) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEmEncerramento(preco)}
                            aria-label={`Encerrar vigência: ${ROTULO_DO_SERVICO[preco.tipoServico]}, ${formatarDinheiro(preco.valor)}`}
                          >
                            <CalendarX2 className="h-4 w-4 sm:mr-2" aria-hidden="true" />
                            <span className="hidden sm:inline">Encerrar vigência</span>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <PaginacaoCompacta
                pagina={pagina}
                tamanho={TAMANHO_DA_PAGINA}
                total={data?.total ?? 0}
                aoMudar={setPagina}
              />
            </>
          )}
        </CardContent>
      </Card>

      {novoAberto && <NovoPrecoDialog onFechar={() => setNovoAberto(false)} />}
      {emEncerramento && (
        <EncerrarVigenciaDialog preco={emEncerramento} onFechar={() => setEmEncerramento(null)} />
      )}
    </div>
  );
}

/** Só preço vigente ou agendado tem vigência a encerrar. */
function podeEncerrar(preco: PrecoPublico): boolean {
  return preco.ativo && (preco.situacao === 'VIGENTE' || preco.situacao === 'AGENDADO');
}

function AvisoDeRecusa({ mensagem }: { mensagem: string | null }) {
  if (!mensagem) {
    return null;
  }
  return (
    <Alert variant="destructive">
      <AlertCircle className="h-4 w-4" aria-hidden="true" />
      <AlertDescription>{mensagem}</AlertDescription>
    </Alert>
  );
}

function NovoPrecoDialog({ onFechar }: { onFechar: () => void }) {
  const cadastrar = useCadastrarPreco();
  const [tipoServico, setTipoServico] = useState<TipoDeServicoAdministrativo | ''>('');
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [decreto, setDecreto] = useState('');
  const [vigenciaInicio, setVigenciaInicio] = useState('');
  const [vigenciaFim, setVigenciaFim] = useState('');
  const [recusa, setRecusa] = useState<string | null>(null);

  function enviar() {
    setRecusa(null);
    if (!tipoServico || descricao.trim().length < 3 || !valor || !vigenciaInicio) {
      setRecusa(
        'Preencha o serviço, a descrição (ao menos 3 letras), o valor e o início da vigência.',
      );
      return;
    }
    if ((paraCentavos(valor) ?? 0) <= 0) {
      setRecusa('Informe um valor maior que zero.');
      return;
    }
    if (vigenciaFim && vigenciaFim < vigenciaInicio) {
      setRecusa('O fim da vigência não pode ser antes do início.');
      return;
    }
    cadastrar.mutate(
      {
        tipoServico,
        descricao: descricao.trim(),
        valor,
        decretoReferencia: decreto.trim() || undefined,
        vigenciaInicio,
        vigenciaFim: vigenciaFim || undefined,
      },
      {
        onSuccess: () => {
          toast.success('Preço cadastrado.');
          onFechar();
        },
        onError: (erro) =>
          setRecusa(
            mensagemDoErro(erro, 'Não foi possível cadastrar o preço agora. Tente de novo.'),
          ),
      },
    );
  }

  return (
    <ResponsiveModal
      open
      onOpenChange={(aberto) => !aberto && onFechar()}
      title="Novo preço público"
      description="O preço passa a valer na data de início da vigência, para os fatos geradores ocorridos a partir dela."
      desktopMaxWidth="max-w-lg"
      footer={
        <>
          <Button variant="outline" onClick={onFechar} disabled={cadastrar.isPending}>
            Cancelar
          </Button>
          <Button onClick={enviar} disabled={cadastrar.isPending}>
            {cadastrar.isPending ? 'Cadastrando…' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 py-2">
        <AvisoDeRecusa mensagem={recusa} />

        <div className="grid gap-2">
          <Label htmlFor="preco-servico">Serviço</Label>
          <Select
            value={tipoServico}
            onValueChange={(escolha) => setTipoServico(escolha as TipoDeServicoAdministrativo)}
          >
            <SelectTrigger id="preco-servico">
              <SelectValue placeholder="Selecione o serviço" />
            </SelectTrigger>
            <SelectContent>
              {TIPOS_DE_SERVICO_ADMINISTRATIVO.map((tipo) => (
                <SelectItem key={tipo} value={tipo}>
                  {ROTULO_DO_SERVICO[tipo]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid gap-2">
          <Label htmlFor="preco-descricao">Descrição</Label>
          <Input
            id="preco-descricao"
            value={descricao}
            maxLength={200}
            onChange={(evento) => setDescricao(evento.target.value)}
            placeholder="O que está sendo cobrado"
          />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="preco-valor">Valor</Label>
            <MoneyInput id="preco-valor" value={valor} onChange={setValor} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="preco-decreto">Decreto de referência</Label>
            <Input
              id="preco-decreto"
              value={decreto}
              maxLength={200}
              onChange={(evento) => setDecreto(evento.target.value)}
              placeholder="opcional"
            />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="preco-vigencia-inicio">Vigência inicia em</Label>
            <Input
              id="preco-vigencia-inicio"
              type="date"
              value={vigenciaInicio}
              onChange={(evento) => setVigenciaInicio(evento.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="preco-vigencia-fim">Vigência termina em</Label>
            <Input
              id="preco-vigencia-fim"
              type="date"
              value={vigenciaFim}
              onChange={(evento) => setVigenciaFim(evento.target.value)}
            />
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
}

function EncerrarVigenciaDialog({
  preco,
  onFechar,
}: {
  preco: PrecoPublico;
  onFechar: () => void;
}) {
  const encerrar = useEncerrarVigencia();
  const [vigenciaFim, setVigenciaFim] = useState('');
  const [recusa, setRecusa] = useState<string | null>(null);

  function enviar() {
    setRecusa(null);
    if (!vigenciaFim) {
      setRecusa('Informe a data em que a vigência termina.');
      return;
    }
    if (vigenciaFim < preco.vigenciaInicio) {
      setRecusa('O fim da vigência não pode ser antes do início.');
      return;
    }
    if (preco.vigenciaFim && vigenciaFim >= preco.vigenciaFim) {
      setRecusa('A vigência só pode ser encurtada: informe uma data anterior à atual.');
      return;
    }
    encerrar.mutate(
      { id: preco.id, vigenciaFim },
      {
        onSuccess: () => {
          toast.success('Vigência encerrada.');
          onFechar();
        },
        onError: (erro) =>
          setRecusa(
            mensagemDoErro(erro, 'Não foi possível encerrar a vigência agora. Tente de novo.'),
          ),
      },
    );
  }

  return (
    <ResponsiveModal
      open
      onOpenChange={(aberto) => !aberto && onFechar()}
      title="Encerrar vigência"
      description={`${ROTULO_DO_SERVICO[preco.tipoServico]} — ${formatarDinheiro(preco.valor)}, desde ${formatarData(preco.vigenciaInicio)}. O preço vale até a data escolhida, inclusive; cobranças já emitidas não mudam.`}
      desktopMaxWidth="max-w-md"
      footer={
        <>
          <Button variant="outline" onClick={onFechar} disabled={encerrar.isPending}>
            Cancelar
          </Button>
          <Button onClick={enviar} disabled={encerrar.isPending}>
            {encerrar.isPending ? 'Encerrando…' : 'Encerrar vigência'}
          </Button>
        </>
      }
    >
      <div className="grid gap-3 py-2">
        <AvisoDeRecusa mensagem={recusa} />
        <div className="grid gap-2">
          <Label htmlFor="preco-encerrar-fim">Vigência termina em</Label>
          <Input
            id="preco-encerrar-fim"
            type="date"
            value={vigenciaFim}
            min={preco.vigenciaInicio}
            onChange={(evento) => setVigenciaFim(evento.target.value)}
          />
        </div>
      </div>
    </ResponsiveModal>
  );
}

export default PrecosPublicosPage;
